/**
 * Phone number helpers.
 *
 * ZIvapp identifies users by phone number, so numbers must be stored in one
 * canonical form or the same person ends up with several accounts.
 * The stored form is always E.164: +2348012345678, digits only after the plus.
 */

/** Default country used when a user types a local number. */
export const DEFAULT_COUNTRY = { code: 'NG', dial: '234', name: 'Nigeria' }

/**
 * Convert user input into E.164, or null when it cannot be understood.
 *
 *   "0801 234 5678"   -> "+2348012345678"
 *   "+234 801 234 5678" -> "+2348012345678"
 *
 * This is deliberately simple. If ZIvapp expands past a handful of countries,
 * replace it with libphonenumber-js rather than growing special cases here.
 *
 * @param {string} input
 * @param {string} [dialCode]  digits only, e.g. '234' — NOT a country code like 'NG'
 * @returns {string|null}
 */
export function toE164(input, dialCode = DEFAULT_COUNTRY.dial) {
  if (!input) return null

  // Passing a country code here ('NG') instead of a dial code ('234') used to
  // produce "+NG8012345678" and store it. Two spellings of one number means two
  // accounts for one person, so refuse rather than guess.
  if (!/^\d+$/.test(String(dialCode))) {
    throw new TypeError(
      `toE164() expects a dial code of digits, e.g. '234'. Received "${dialCode}". ` +
      `For a country code, use DEFAULT_COUNTRY.dial or a lookup.`
    )
  }

  const trimmed = String(input).trim()
  const hadPlus = trimmed.startsWith('+')
  let digits = trimmed.replace(/\D/g, '')
  if (!digits) return null

  if (hadPlus) return `+${digits}`

  // 00 is the international prefix in much of the world
  if (digits.startsWith('00')) return `+${digits.slice(2)}`

  // A leading 0 is a national trunk prefix and is dropped before the dial code
  if (digits.startsWith('0')) digits = digits.slice(1)

  // Already carries the dial code and is long enough to be complete
  if (digits.startsWith(dialCode) && digits.length >= dialCode.length + 7) {
    return `+${digits}`
  }

  return `+${dialCode}${digits}`
}

/** Loose sanity check — not a guarantee the number exists. */
export function isValidE164(value) {
  return typeof value === 'string' && /^\+[1-9]\d{7,14}$/.test(value)
}

/** Readable form for the UI: "+234 801 234 5678". */
export function formatDisplay(e164) {
  if (!isValidE164(e164)) return e164 ?? ''
  const digits = e164.slice(1)
  const groups = digits.slice(3).replace(/(\d{3})(?=\d)/g, '$1 ')
  return `+${digits.slice(0, 3)} ${groups}`.trim()
}

/** Last digits only, for display where the full number is too much. */
export function maskNumber(e164, visible = 4) {
  if (!e164) return ''
  const tail = e164.slice(-visible)
  return `${'*'.repeat(Math.max(0, e164.length - visible - 1))}${tail}`
}
